import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, 
  Timer, 
  Sparkles, 
  PlayCircle, 
  Users, 
  Zap, 
  Calendar, 
  Shield, 
  Lock, 
  Globe, 
  Award, 
  CheckCircle, 
  Star, 
  FileText, 
  X,
  Verified,
  TrendingUp,
  Clock,
  Eye,
  MousePointerClick,
  Loader2
} from 'lucide-react';
import { toast } from "@/components/ui/use-toast";
import { stripeService } from "@/services/stripeService";
import { api } from "@/lib/api";

const LockInLanding = () => {
  const [email, setEmail] = useState('');
  const [availableSeats, setAvailableSeats] = useState(10);
  const [liveSends, setLiveSends] = useState(142891);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [timeOnPage, setTimeOnPage] = useState(0);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  const navigate = useNavigate();

  // Live counter animations
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveSends(prev => prev + Math.floor(Math.random() * 8) + 2);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Beta seat countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setAvailableSeats(prev => Math.max(prev - Math.floor(Math.random() * 2), 1));
    }, 60000); // Every minute
    return () => clearInterval(interval);
  }, []);

  // Time on page tracker
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeOnPage(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Scroll tracking for exit intent and floating button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / documentHeight) * 100;
      setScrollPercent(scrollPercent);
      
      // Show floating button after scrolling 20% down the page
      setShowFloatingButton(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Exit intent popup logic
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && (timeOnPage > 30 || scrollPercent > 50) && !showExitIntent && !emailCaptured) {
        setShowExitIntent(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [timeOnPage, scrollPercent, showExitIntent, emailCaptured]);

  const handleEmailCapture = () => {
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    
    console.log('🔥 Email capture triggered:', email);
    setEmailCaptured(true);
    
    // Store email for checkout flows
    localStorage.setItem('lockInEmail', email);
    
    // Show success message and scroll to pricing
    toast({
      title: "Email Captured!",
      description: "Choose your plan below to lock in beta pricing.",
      variant: "default",
    });
  };

  const handleExitEmailCapture = () => {
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    
    setEmailCaptured(true);
    setShowExitIntent(false);
    
    // Store email for checkout flows
    localStorage.setItem('lockInEmail', email);
    
    toast({
      title: "Email Captured!",
      description: "Choose your plan below to lock in beta pricing.",
      variant: "default",
    });
  };

  // Handle direct plan selection and checkout
  const handlePlanSelect = async (planKey: string) => {
    console.log('🎯 Direct plan selection for:', planKey);
    
    // Use stored email if available, otherwise use form email
    const userEmail = email.trim() || localStorage.getItem('lockInEmail') || '';
    
    if (!userEmail) {
      toast({
        title: "Email Required",
        description: "Please enter your email first to proceed with checkout.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsProcessing(true);
      setProcessingPlan(planKey);
      
      let checkoutResponse;
      
      // Handle beta plans with custom pricing
      if (planKey === 'custom_starter_beta') {
        console.log('🚀 Creating Stripe checkout for Starter Beta at $97');
        
        // Create custom checkout for Starter Beta
        const customResponse = await api.post("/auth/initiate-stripe-checkout", {
          email: userEmail,
          planId: "custom_starter_beta",
          planName: "Starter Beta",
          planPrice: 97,
          planInterval: "month",
          isDirectCheckout: true
        });
        
        checkoutResponse = customResponse.data;
        
      } else if (planKey === 'custom_pro_beta') {
        console.log('🚀 Creating Stripe checkout for Pro Beta at $297');
        
        // Create custom checkout for Pro Beta
        const customResponse = await api.post("/auth/initiate-stripe-checkout", {
          email: userEmail,
          planId: "custom_pro_beta", 
          planName: "Pro Beta",
          planPrice: 297,
          planInterval: "month",
          isDirectCheckout: true
        });
        
        checkoutResponse = customResponse.data;
        
      } else if (planKey === 'usage') {
        // Free plan - redirect to register with email
        navigate(`/register?plan=usage&email=${encodeURIComponent(userEmail)}`);
        return;
        
      } else {
        // Use existing stripe service for regular plans
        console.log('🚀 Creating Stripe checkout for plan:', planKey);
        checkoutResponse = await stripeService.initiateCheckout(
          userEmail,
          planKey
        );
      }
      
      // Redirect to Stripe checkout
      if (checkoutResponse?.checkoutUrl) {
        console.log('✅ Redirecting to Stripe checkout:', checkoutResponse.checkoutUrl);
        window.location.href = checkoutResponse.checkoutUrl;
      } else {
        throw new Error('No checkout URL received from payment processor');
      }
      
    } catch (error) {
      console.error('❌ Error during plan selection:', error);
      
      let errorMessage = "Failed to process plan selection. Please try again.";
      let shouldFallback = false;
      
      if (error.message?.includes("Invalid plan")) {
        errorMessage = "The selected plan is not available. Please choose a different plan.";
      } else if (error.message?.includes("payment service")) {
        errorMessage = "Payment service is temporarily unavailable. Please try again later.";
        shouldFallback = true;
      } else if (error.response?.status >= 500) {
        errorMessage = "Payment processing error. Please contact support if this persists.";
        shouldFallback = true; 
      } else if (error.response?.status === 404) {
        errorMessage = "Payment service is not available. Please try again later.";
        shouldFallback = true;
      }
      
      // Fallback: redirect to registration page if API is down
      if (shouldFallback && planKey !== 'usage') {
        console.log('🔄 Falling back to registration page');
        
        toast({
          title: "Redirecting",
          description: "Taking you to complete your registration...",
          variant: "default",
        });
        
        // Use registration fallback with plan details
        const planParams = new URLSearchParams({
          email: userEmail,
          plan: planKey,
          directCheckout: 'true'
        });
        
        if (planKey === 'custom_starter_beta') {
          planParams.set('planName', 'Starter Beta');
          planParams.set('price', '97');
        } else if (planKey === 'custom_pro_beta') {
          planParams.set('planName', 'Pro Beta');
          planParams.set('price', '297');
        }
        
        navigate(`/register?${planParams.toString()}`);
        return;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setProcessingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white overflow-hidden">
      {/* Hero Section with Optimizations */}
      <section className="relative pt-8 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* Navigation */}
          <nav className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 flex items-center justify-center">
                <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-8 w-8">
                  <g>
                    {/* Left wing */}
                    <path d="M20 50 L70 0 L70 70 L50 90 L50 150 L70 130 L70 200 L20 150 Z" fill="#3B82F6"/>
                    {/* Center V */}
                    <path d="M70 70 L100 40 L130 70 L100 100 Z" fill="#60A5FA"/>
                    {/* Right wing */}
                    <path d="M130 70 L130 0 L180 50 L180 150 L130 200 L130 130 L150 150 L150 90 Z" fill="#3B82F6"/>
                  </g>
                </svg>
              </div>
              <span className="font-bold text-xl text-white">MUNGER</span>
            </div>
            <Button 
              onClick={() => navigate('/login')}
              variant="outline" 
              className="bg-transparent border-white/30 text-white hover:bg-white/10"
            >
              Sign In
            </Button>
          </nav>

          {/* Hard Beta Counter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <Badge className="bg-red-500/20 text-red-300 border-red-500/30 px-4 py-2">
                <Timer className="w-4 h-4 mr-2" />
                <span className="font-mono text-lg font-bold">{Math.min(availableSeats, 10)}</span>
                <span className="mx-1">/</span>
                <span className="text-sm">100 waitlist spots</span>
              </Badge>
              <div className="flex items-center gap-2 text-green-400">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-sm">Refreshes in real-time</span>
              </div>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
                The Fully
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {" "}Automated
                </span>
                <br />
                Email Marketer
              </h1>
              
              {/* 1. Sharpened Promise - Quantified Outcome */}
              <p className="text-2xl text-gray-200 mb-8 leading-relaxed font-medium">
                Send up to 1,000,000 emails a day—hands-free.
              </p>

              {/* 2. Inline Email Capture */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-8"
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                  <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-400" />
                    Lock In Beta Pricing
                  </h3>
                  <p className="text-sm text-yellow-300 mb-4 font-medium">
                    ⚡ 100 waitlist spots only • Pricing goes up after beta
                  </p>
                  <div className="flex gap-3">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your work email"
                      className="flex-1 bg-white/20 border-white/30 text-white placeholder-gray-300 focus:border-blue-400"
                      onKeyPress={(e) => e.key === 'Enter' && handleEmailCapture()}
                    />
                    <Button
                      onClick={handleEmailCapture}
                      className="bg-[#20e3b2] hover:bg-[#1bc9a0] text-black font-semibold px-6"
                      disabled={!email.trim()}
                    >
                      Lock In
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Reserve your spot • Current pricing locked forever • Price increases post-beta
                  </p>
                </div>
              </motion.div>

              {/* 7. Comprehensive Feature Badges */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-8"
              >
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
                  <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                    <TrendingUp className="w-3 h-3 text-green-400" />
                    <span className="text-green-300 font-medium">25-45% open rates</span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2">
                    <Clock className="w-3 h-3 text-blue-400" />
                    <span className="text-blue-300 font-medium">2-4 min to launch</span>
                  </div>
                  <div className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-lg px-3 py-2">
                    <Users className="w-3 h-3 text-purple-400" />
                    <span className="text-purple-300 font-medium">3B contacts included</span>
                  </div>
                  <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2">
                    <Zap className="w-3 h-3 text-orange-400" />
                    <span className="text-orange-300 font-medium">Self-improving AI</span>
                  </div>
                  <div className="flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded-lg px-3 py-2">
                    <Award className="w-3 h-3 text-pink-400" />
                    <span className="text-pink-300 font-medium">100M+ emails trained</span>
                  </div>
                  <div className="flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg px-3 py-2">
                    <Eye className="w-3 h-3 text-cyan-400" />
                    <span className="text-cyan-300 font-medium">Pixel tracking learns</span>
                  </div>
                  <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                    <Sparkles className="w-3 h-3 text-red-400" />
                    <span className="text-red-300 font-medium">First AI-first system</span>
                  </div>
                  <div className="flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-3 py-2">
                    <Calendar className="w-3 h-3 text-indigo-400" />
                    <span className="text-indigo-300 font-medium">Built-in booking</span>
                  </div>
                  <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                    <ArrowRight className="w-3 h-3 text-emerald-400" />
                    <span className="text-emerald-300 font-medium">Zero-click importing</span>
                  </div>
                  <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2">
                    <MousePointerClick className="w-3 h-3 text-yellow-400" />
                    <span className="text-yellow-300 font-medium">Manual override</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: Above-the-fold Demo */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* 3. Above-the-fold Demo Video */}
              <div className="relative bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl p-6 border border-blue-500/30">
                <div className="flex items-center gap-3 mb-4">
                  <PlayCircle className="w-6 h-6 text-blue-400" />
                  <h3 className="text-lg font-semibold text-white">90-Second Demo</h3>
                  <Badge className="bg-green-500/20 text-green-300 text-xs">LIVE</Badge>
                </div>
                <div className="aspect-video bg-gray-800/50 rounded-xl relative overflow-hidden">
                  {/* Loom Demo Video Embed */}
                  <div style={{position: 'relative', paddingBottom: '64.91862567811934%', height: 0}}>
                    <iframe 
                      src="https://www.loom.com/embed/ffb1ea4969cf484fbdd2e0cd0ef43ff7?sid=e37c09d3-2a8f-4e40-a483-991bba820c7a" 
                      frameBorder="0" 
                      allowFullScreen={true}
                      style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-300 mt-3">
                  See how AI handles everything from finding prospects to booking meetings automatically
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. Social Proof Bar with Results & Impact */}
      <section className="py-16 bg-black/20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-8">
              Results & <span className="text-green-400">Impact</span>
            </h2>
            
            {/* Stats with Testimonials */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">1,847</div>
                <div className="text-sm text-gray-300">Hours saved this week</div>
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <p className="text-sm text-gray-300 mb-2">"Finally touching grass instead of email campaigns"</p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">S</div>
                    <span className="text-xs text-gray-400">Sarah M. - Product Builder</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">$2.4M</div>
                <div className="text-sm text-gray-300">Revenue generated</div>
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <p className="text-sm text-gray-300 mb-2">"Now I focus on building, not marketing"</p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white text-xs">M</div>
                    <span className="text-xs text-gray-400">Marcus R. - Founder</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-400 mb-2">98%</div>
                <div className="text-sm text-gray-300">Automation rate</div>
                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <p className="text-sm text-gray-300 mb-2">"Set up once, revenue flows forever"</p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs">E</div>
                    <span className="text-xs text-gray-400">Emily W. - Inventor</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Logo Bar */}
            <div className="flex items-center justify-center space-x-12 opacity-80 mb-8">
              {[
                { name: 'Stripe', testimonial: 'Scaled to 10k/day' },
                { name: 'HubSpot', testimonial: '94% deliverability' },
                { name: 'TechFlow', testimonial: '12x ROI achieved' },
                { name: 'Linear', testimonial: '300+ meetings/month' },
                { name: 'Notion', testimonial: 'Zero setup needed' }
              ].map((company, index) => (
                <div key={index} className="text-center">
                  <div className="text-gray-400 font-semibold text-lg mb-1">{company.name}</div>
                  <div className="text-xs text-gray-500">{company.testimonial}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-8">
              <div className="flex items-center gap-2 text-green-400">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="font-mono text-sm">Live counter</span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
                <span className="text-sm text-gray-300 ml-2">4.9/5 on Trustpilot</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. 3-Step Benefit Stack */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              3 Steps to <span className="text-green-400">Touch Grass</span>
            </h2>
            <p className="text-xl text-gray-300">Describe your product → AI handles everything → Revenue flows</p>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                step: "①",
                title: "Describe your product",
                description: "Just tell AI what you sell—no contact lists, no setup, no technical work required",
                metric: "2-4 minutes to complete setup",
                color: "from-blue-500 to-purple-600",
                icon: Sparkles
              },
              {
                step: "②", 
                title: "AI handles everything",
                description: "Finds 3B+ contacts, sets up servers, writes hyper-personalized emails, learns what works",
                metric: "100% automated workflow",
                color: "from-purple-500 to-pink-600",
                icon: Zap
              },
              {
                step: "③",
                title: "Revenue flows while you live",
                description: "Meetings book automatically while you focus on family, products, and things you love",
                metric: "Save 1000s of hours annually",
                color: "from-pink-500 to-green-600",
                icon: TrendingUp
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="flex items-center gap-6 p-6 bg-white/5 rounded-2xl border border-white/10"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${step.color} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-300 mb-2">{step.description}</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-medium text-green-400">{step.metric}</span>
                  </div>
                </div>
                <step.icon className="w-8 h-8 text-gray-400" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section with High-Contrast CTAs - ORIGINAL BETA PRICING */}
      <section className="py-16 px-4 bg-black/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Post-Beta Pricing Preview</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              These will be the prices after beta ends. Current beta users are locked in at lower rates.
            </p>
            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg max-w-xl mx-auto">
              <p className="text-yellow-300 font-semibold">💰 Beta users saved thousands with current pricing</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Starter Plan */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Starter</h3>
                <div className="text-4xl font-bold text-white">$291</div>
                <div className="text-sm text-gray-400">150K emails/month</div>
                <div className="text-xs text-green-300 mt-2">Beta users pay only $97</div>
              </div>
              <ul className="space-y-3 text-sm text-left mb-8">
                <li className="flex items-center text-gray-300">
                  <CheckCircle className="w-4 h-4 mr-3 text-green-400" />
                  3B contacts included
                </li>
                <li className="flex items-center text-gray-300">
                  <CheckCircle className="w-4 h-4 mr-3 text-green-400" />
                  AI finds prospects
                </li>
                <li className="flex items-center text-gray-300">
                  <CheckCircle className="w-4 h-4 mr-3 text-green-400" />
                  Self-improving system
                </li>
              </ul>
              <Button 
                onClick={() => handlePlanSelect('custom_starter_beta')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                disabled={isProcessing && processingPlan === 'custom_starter_beta'}
              >
                {isProcessing && processingPlan === 'custom_starter_beta' ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Creating Checkout...
                  </>
                ) : (
                  'Get Beta Price ($97)'
                )}
              </Button>
            </div>

            {/* Pro Plan */}
            <div className="bg-white/5 border-2 border-[#20e3b2] rounded-xl p-6 text-center relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-[#20e3b2] text-black text-sm px-3 py-1 rounded-full font-semibold">
                  Most Popular
                </span>
              </div>
              <div className="text-white text-lg font-semibold mb-2">Pro</div>
              <div className="text-4xl font-bold text-white mb-2">$297</div>
              <div className="text-sm text-gray-300 mb-4">500K emails/month</div>
              <div className="text-xs text-gray-400 mb-6">Will be $891 post-beta</div>
              <div className="relative">
                <Button 
                  onClick={() => handlePlanSelect('custom_pro_beta')}
                  size="lg"
                  className="bg-[#20e3b2] hover:bg-[#1bc9a0] text-black font-bold text-xl px-12 py-8 shadow-2xl relative z-10 w-full min-w-[350px]"
                  disabled={isProcessing && processingPlan === 'custom_pro_beta'}
                >
                  {isProcessing && processingPlan === 'custom_pro_beta' ? (
                    <>
                      <Loader2 className="animate-spin mr-3 h-5 w-5" />
                      Creating Checkout...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-3 h-5 w-5" />
                      Lock In Pro Plan - $297/mo
                    </>
                  )}
                </Button>
                <div className="absolute -inset-1 bg-gradient-to-r from-[#20e3b2] to-blue-600 rounded-lg blur-sm opacity-75 animate-pulse"></div>
              </div>
            </div>

            {/* Scale Plan */}
            <div className="bg-white/5 border border-white/20 rounded-xl p-6 text-center">
              <div className="text-white text-lg font-semibold mb-2">Scale</div>
              <div className="text-4xl font-bold text-white mb-2">$997</div>
              <div className="text-sm text-gray-300 mb-4">5M emails/month</div>
              <div className="text-xs text-gray-400 mb-6">Will be $2,991 post-beta</div>
              <Button 
                onClick={() => handlePlanSelect('growth')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                disabled={isProcessing && processingPlan === 'growth'}
              >
                {isProcessing && processingPlan === 'growth' ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Creating Checkout...
                  </>
                ) : (
                  'Get Beta Price ($997)'
                )}
              </Button>
            </div>

            {/* Custom Plan */}
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6 text-center">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Custom</h3>
                <div className="text-4xl font-bold text-white">$?</div>
                <div className="text-sm text-gray-400">Unlimited everything</div>
                <div className="text-xs text-red-300 mt-2">Custom pricing</div>
              </div>
              <ul className="space-y-3 text-sm text-left mb-8">
                <li className="flex items-center text-gray-300">
                  <CheckCircle className="w-4 h-4 mr-3 text-green-400" />
                  Custom AI models
                </li>
                <li className="flex items-center text-gray-300">
                  <CheckCircle className="w-4 h-4 mr-3 text-green-400" />
                  Dedicated servers
                </li>
                <li className="flex items-center text-gray-300">
                  <CheckCircle className="w-4 h-4 mr-3 text-green-400" />
                  Enterprise support
                </li>
              </ul>
              <Button 
                onClick={() => handlePlanSelect('custom')}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                disabled={isProcessing && processingPlan === 'custom'}
              >
                {isProcessing && processingPlan === 'custom' ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Processing...
                  </>
                ) : (
                  'Contact Sales'
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="bg-red-500/20 text-red-300 border-red-500/30 mb-6">
              <Timer className="w-4 h-4 mr-2" />
              {Math.min(availableSeats, 10)} waitlist spots remaining
            </Badge>
            
            <h2 className="text-5xl font-bold text-white mb-6">
              <span className="text-green-400">Type To Scale</span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-8">
              Stop clicking. Start living. Let AI handle your marketing while you build what matters.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => handlePlanSelect('custom_pro_beta')}
                className="bg-[#20e3b2] hover:bg-[#1bc9a0] text-black font-semibold px-8 py-4 text-lg"
                disabled={isProcessing && processingPlan === 'custom_pro_beta'}
              >
                {isProcessing && processingPlan === 'custom_pro_beta' ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-5 w-5" />
                    Creating Checkout...
                  </>
                ) : (
                  <>
                    Lock In Beta Pricing
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              <Button 
                size="lg"
                onClick={() => navigate('/login')}
                variant="outline" 
                className="bg-transparent border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Sign In
              </Button>
            </div>
            
            <p className="text-sm text-gray-400 mt-4">
              Beta pricing locked forever • Only 100 spots • Pricing increases post-beta
            </p>
          </motion.div>
        </div>
      </section>

      {/* 8. Exit-Intent Lead Magnet Popup */}
      <AnimatePresence>
        {showExitIntent && !emailCaptured && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-white/10 max-w-lg w-full relative"
            >
              <Button
                onClick={() => setShowExitIntent(false)}
                variant="ghost"
                size="sm"
                className="absolute top-4 right-4 text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Get the case study 📊</h3>
                <p className="text-gray-300">
                  <strong>How we booked 7,200 sales meetings</strong> using this exact cold email system.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <h4 className="text-green-400 font-semibold mb-2">Inside the case study:</h4>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>• Email templates that got 15% reply rates</li>
                    <li>• Exact targeting criteria for each industry</li>
                    <li>• Deliverability secrets that bypass spam filters</li>
                    <li>• Follow-up sequences that convert 4x better</li>
                  </ul>
                </div>
                
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email for instant download"
                  className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                  onKeyPress={(e) => e.key === 'Enter' && handleExitEmailCapture()}
                />
                
                <Button
                  onClick={handleExitEmailCapture}
                  className="w-full bg-[#20e3b2] hover:bg-[#1bc9a0] text-black font-semibold"
                  disabled={!email.trim()}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download Case Study Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  Free PDF download • No spam • Instant access
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating CTA Button */}
      <AnimatePresence>
        {showFloatingButton && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-6 right-6 z-40"
          >
            <Button
              onClick={() => handlePlanSelect('custom_pro_beta')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-white/20"
              disabled={isProcessing && processingPlan === 'custom_pro_beta'}
            >
              {isProcessing && processingPlan === 'custom_pro_beta' ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-5 w-5" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Get Beta Pricing
                  <Sparkles className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="bg-black/40 py-8 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-sm text-gray-400">© 2024 Munger.ai. All rights reserved.</p>
            </div>
            <div className="flex space-x-6">
              <Button
                onClick={() => navigate('/login')}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/register')}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                Sign Up
              </Button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LockInLanding; 